/**
 * Step-progress navigation menu displayed at the top of the new-SIM creation wizard.
 *
 * @module components/common/NewSimMenu
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles, withStyles} from '@material-ui/core/styles';
import {connect} from 'react-redux';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepConnector from '@material-ui/core/StepConnector';
import Button from '@material-ui/core/Button';
import Toolbar from '@material-ui/core/Toolbar';
import NewSimMenuStyle from './NewSimMenu';

/**
 * QontoConnector is a styled version of the StepConnector component.
 * It uses custom styling to modify the appearance and behavior of
 * the connector in a stepper component.
 *
 * The root style has been customized to hide the connector by setting
 * its `display` property to 'none'.
 *
 * This component can be used to create a visually distinct stepper
 * without showing the connection lines between steps.
 *
 * @type {StyledComponent}
 */
const QontoConnector = withStyles({
    root: {
        display: 'none',
    },
})(StepConnector);

/**
 * Component that renders a navigation menu to manage the steps of a simulation process.
 *
 * @param {Object} props - The props object for the component.
 * @param {Object} props.newSim - The state object containing the active step of the simulation.
 * @param {number} props.newSim.activeStep - The current active step of the simulation process.
 * @param {Array.<string>} props.simProductType - Array defining the product types associated with the simulation.
 *      Determines the steps displayed in the menu, e.g., "Voice Only".
 *
 * @returns {JSX.Element} A toolbar containing a stepper component for navigation between steps
 *      and an optional button for displaying a tutorial at a specific step.
 */
const NewSimMenu = ({newSim, simProductType}) => {
    const useStyles = makeStyles((theme) => NewSimMenuStyle(theme));
    const classes = useStyles();
    const {activeStep} = newSim;
    const steps = [
        'Basic Info',
        'Select Avatars',
        'Create Levels',
        'Data Input'
    ];

    return (
        <Toolbar className={classes.root}>
            <div>
                <Stepper
                    activeStep={activeStep}
                    connector={<QontoConnector/>}
                    className={classes.stepperRoot}
                >
                    {steps.map((label, index) => {
                        if (simProductType.includes('Voice Only') && index === 3) {
                            return false;
                        }
                        return (
                            <Step
                                key={label}
                                className={index <= activeStep ? classes.stepActive : classes.step}
                            >
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
            </div>
            <div>
                {activeStep === 2 ? <Button variant="outlined" className={classes.tutorialBtn}>TUTORIAL</Button> : null}
            </div>
        </Toolbar>
    );
};

/**
 * PropTypes validation for the NewSimMenu component.
 *
 * The NewSimMenu component requires the following props with specified types:
 *
 * @property {function} onSimulate - A callback function that is triggered when a simulation is started.
 * @property {function} onLoad - A callback function that is triggered when loading data or a resource.
 * @property {function} onSave - A callback function that is triggered when saving data or a resource.
 * @property {boolean} isSimulationRunning - A boolean flag indicating whether a simulation is currently running.
 */
NewSimMenu.propTypes = {
    newSim: PropTypes.shape({
        activeStep: PropTypes.number,
    }),
    simProductType: PropTypes.string
};

/**
 * Maps the state from the Redux store to the component's props.
 *
 * @param {Object} state - The current Redux store state.
 * @returns {Object} An object containing specific parts of the Redux state
 *                   to be injected as props into a component.
 *                   - `newSim`: Represents the `newSim` property from `state.sim`.
 *                   - `simProductType`: Represents the `currentSimProductType` property from `state.sim`.
 */
const mapStateToProps = (state) => ({
    newSim: state.sim.newSim,
    simProductType: state.sim.currentSimProductType,
});

export default connect(mapStateToProps)(NewSimMenu);
